import type { BannerColorVariation } from '@cash-rewards/cr-ui-components';

import { BrazeContentCard } from '@/modules/shop/models/braze';

export interface OfferCoreModel {
  id: number;
  isAdvert: boolean;
}
export interface OfferModel extends OfferCoreModel {
  title: string;
  couponCode: string;
  endDateTime: string;
  endDateTimeOffset: string;
  description: string;
  hyphenatedString: string;
  isFeatured: boolean;
  terms: string;
  merchantId: number;
  merchantLogoUrl: string;
  offerBackgroundImageUrl: string;
  offerBadge: string;
  isCashbackIncreased: boolean;
  isPremiumFeature: boolean;
  wasRate: string;
  merchant: OfferMerchantModel;
  premium: null;
  clientCommissionString: string;
  regularImageUrl: string;
  merchantHyphenatedString: string;
  isPersonalised: boolean;
  isAdvert: false;
  cashbackCapDescription?: string;
  bannerTCs?: string | null;
  flashSalesBadgeOverride?: string;
  colorVariant?: BannerColorVariation;
}

export interface Banner {
  mobileLink: string;
  mobileImageUrl: string;
  desktopLink: string;
  desktopImageUrl: string;
  mobileBrowserLink: string;
  mobileBrowserImageUrl: string;
  id: number | string;
  name: string;
  impressionUrl: string | null;
  adSizeId: number;
  advertiserId: number;
  advertiserName: string | null;
  flightId: number;
  campaignId: number;
  rawClickUrl: string | null;
  keywords: string[] | null;
  priorityWeight: number;
  merchantCommissionString: string | null;
  merchantDescription: string | null;
  merchantName: string | null;
  brazeContentCardInstance?: BrazeContentCard;
  eventUrls?: Record<string, string>;
  bannerTCs?: string | null;
  flashSalesBadgeOverride?: string | null;
  colorVariant?: BannerColorVariation;
}

interface RetailMediaADBanner {
  bannerTitle: string;
  highlightCopyInPurpleLight: string;
  merchantLogoOverride: string;
  termsAndConditions: string;
  brandNameInBold: string;
  subCopy: string;
  highlightCopyInPurpleBold: string;
  merchantLogoUrl: string;
  isDynamic: boolean;
  singleTierEndDate?: string;
  cashbackCap?: string;
}

export interface OfferBannerAd extends Banner, Partial<RetailMediaADBanner> {
  isAdvert: true;
}

export interface OfferAdModel extends OfferCoreModel {
  $type: string;
  id: number;
  adName: string;
  adDesktopImageUrl: string;
  adImpressionUrl: string;
  adLink: string;
  adMobileImageUrl: string;
  adSizeId: number;
  advertiserId: number;
  advertiserName: string;
  campaignId: number;
  eventUrls?: Record<string, string>;
  flightId: number;
  isAdvert: true;
  keywords: string[];
  merchantCommissionString: string;
  merchantDescription: string;
  merchantName: string;
  priorityWeight: number;
  rawClickUrl: string;
  brandNameInBold: string;
  subCopy: string;
  highlightCopyInPurpleBold: string;
  adLabel: string;
  merchantLogoOverride?: string;
  isDynamic?: boolean;
  merchantLogoUrl?: string;
  singleTierEndDate?: string;
}

export type Offer = OfferModel | OfferAdModel;

export interface OfferMerchantModel {
  id: number;
  name: string;
  hyphenatedString: string;
  logoUrl: string;
  description: string;
  commission: number;
  isFlatRate: boolean;
  commissionType: string;
  offerCount: number;
  rewardType: string;
  isCustomTracking: boolean;
  backgroundImageUrl: string;
  merchantBadge: string;
  desktopEnabled: boolean;
  isEonxGiftCard?: boolean;
  firstTierMinimumSpendString?: string;
}

export type OfferBanner = OfferModel | OfferBannerAd;

export type ShopBanner = OfferBanner | Banner;
export interface SecondaryBanner extends RetailMediaADBanner {
  adPosition: number;
  adSizeId: number;
  advertiserId: number;
  advertiserName: string;
  campaignId: number;
  clickUrl: string;
  contentUrl: string;
  desktop: string;
  eventUrls?: Record<string, string>;
  fileName: string;
  flightId: number;
  height: number;
  id: number;
  impressionUrl: string;
  keywords: string[] | null;
  merchantCommissionString: null;
  merchantDescription: null;
  merchantName: null;
  mobile: string;
  priorityWeight: number;
  rawClickUrl: string;
  tablet: string;
  title: string;
  width: number;
  singleTierEndDate?: string;
  colorVariant?: BannerColorVariation;
}

export interface SecondaryBanners {
  'shop-secondary': Array<SecondaryBanner>;
}

export interface SecondaryBannerResponse<T> {
  data: T;
}

export interface Response<T> {
  data: T[];
  totalCount: number;
  count: number;
}

export interface PersonalisedStoreResponseModel extends Response<StoreModel> {
  algorithmId: string;
  algorithmVersion: string;
}

export interface PersonalisedOfferResponseModel extends Response<Offer> {
  algorithmId: string;
  algorithmVersion: string;
  experimentIds: string[];
}

export type Store = StoreModel | StoreAdModel;

export interface StoreModel {
  merchantId: number;
  hyphenatedString: string;
  logoUrl: string;
  backgroundImageUrl: string;
  clientCommissionString: string;
  merchantBadge: string | null;
  firstTierSpecialTerms: string | null;
  isEonxGiftCard?: boolean;
  name: string;
  isAdvert: boolean;
  cashbackCapDescription?: string;
  firstTierMinimumSpendString?: string;
}

export interface StoreAdModel {
  isAdvert: boolean;
  id: number;
  adImpressionUrl?: string;
  adClickUrl?: string;
  eventUrls?: Record<string, string>;
  advertiserId: number;
  campaignId: number;
  flightId: number;
  rawClickUrl: string;
  keywords: string[];
  advertiserName: string;
  priorityWeight: number;
  adSizeId: number;
  merchantName?: string;
  merchantDescription?: string;
  merchantCommissionString?: string;
  adHeight: number;
  adWidth: number;
  externalUrl?: string;
  merchantLogo?: string;
  merchantLogoOverride: string;
  title: string;
  brandNameInBold: string;
  contentUrl: string;
  highlightCopyInPurpleBold: string;
}

export const isStoreModel = (
  merchantOffer: OfferModel | StoreModel
): merchantOffer is StoreModel =>
  (merchantOffer as OfferModel).merchant === undefined;

export interface CarouselsResponse {
  carousels: CarouselModel[];
}

export interface CarouselModel {
  $type: string;
  algorithmId: string;
  algorithmVersion: string;
  experimentIds: string[];
  categoryId: number | null;
  ctegoryName: string;
  data: (Offer | Store | OfferBannerAd)[];
  totalCount: number;
  count: number;
  page: number;
  offerPlacement: number;
}
